// src/services/chatWebSocket.js
/**
 * WebSocket service for real-time chat and status notifications
 * wss://aeroviewbackend.dockyardsoftware.com:8095/chat
 */

class ChatWebSocket {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 2000;
    this.authKey = null;
    this.userId = null; // Employee ID or Driver SNo
    this.listeners = []; // Support multiple listeners
    this.WS_URL = "wss://aeroviewbackend.dockyardsoftware.com:8095/chat";
  }

  /**
   * Connect to chat WS server
   * @param {string} authKey - authentication token
   * @param {string} userId - User ID (ServiceNo for driver, or Employee ID)
   * @param {string} role - "Driver" or "Employee"
   */
  connect(authKey, userId, role) {
    if (!authKey || !userId) {
      console.warn("[ChatWS] Missing authKey or userId");
      return;
    }

    this.authKey = authKey;
    this.userId = userId;

    // Check if already connected with SAME credentials
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      if (this.currentRole === role && this.userId === userId) {
        console.log("[ChatWS] Already connected as", role);
        return;
      }
      console.log("[ChatWS] Role/User changed, reconnecting...");
    }

    this.disconnect();
    this.currentRole = role; // Store current role

    try {
      // Append token if needed by backend, though standard is usually just the URL
      const url = this.WS_URL + "?token=" + encodeURIComponent(authKey) + "&id=" + encodeURIComponent(userId) + "&role=" + encodeURIComponent(role);
      console.log("[ChatWS] Connecting to:", this.WS_URL);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.log("[ChatWS] Connected!");

        // Send initial auth/register message if required by this specific backend
        // Assuming a standard structure based on other services
        try {
          this.ws.send(JSON.stringify({
            type: "register",
            userId: userId,
            role: role,
            token: authKey
          }));
        } catch (e) {
          console.error("[ChatWS] Register send failed:", e);
        }
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          console.log("[ChatWS] Received:", msg);
          
          // Notify all listeners
          this.listeners.forEach(callback => callback(msg));
          
        } catch (e) {
          console.log("[ChatWS] Received (text):", evt.data);
        }
      };

      this.ws.onerror = (err) => {
        console.error("[ChatWS] Error:", err);
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        console.log("[ChatWS] Closed");
        this.attemptReconnect(role);
      };
    } catch (err) {
      console.error("[ChatWS] Failed to create WebSocket:", err);
      this.attemptReconnect(role);
    }
  }

  /**
   * Send Emergency Alert
   * @param {object} data - { vehicleId, ecode, ediscription, driverName }
   */
  sendEmergency(data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("[ChatWS] Cannot send Emergency - not connected");
      return false;
    }

    try {
      const message = {
        type: "EMERGENCY_ALERT",
        ...data,
        timestamp: new Date().toISOString(),
      };
      this.ws.send(JSON.stringify(message));
      console.log("[ChatWS] Emergency Sent:", message);
      return true;
    } catch (e) {
      console.error("[ChatWS] Failed to send Emergency:", e);
      return false;
    }
  }

  /**
   * Send a status update (Accept/Decline)
   * @param {object} data - { bId, status, message, targetUserId }
   */
  sendStatus(data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("[ChatWS] Cannot send - not connected");
      return false;
    }

    try {
      const message = {
        type: "status_update",
        ...data,
        timestamp: new Date().toISOString(),
      };
      this.ws.send(JSON.stringify(message));
      console.log("[ChatWS] Sent:", message);
      return true;
    } catch (e) {
      console.error("[ChatWS] Failed to send:", e);
      return false;
    }
  }

  // Add a listener
  onMessage(callback) {
    if (typeof callback === 'function') {
      this.listeners.push(callback);
      // Return unsubscribe function
      return () => this.offMessage(callback);
    }
    return () => {};
  }
  
  // Remove a listener
  offMessage(callback) {
    this.listeners = this.listeners.filter(cb => cb !== callback);
  }

  attemptReconnect(role) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    setTimeout(() => {
      this.connect(this.authKey, this.userId, role);
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  disconnect() {
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {}
      this.ws = null;
    }
    this.isConnected = false;
    // Don't clear listeners automatically, or components will lose connection on reconnect
    // this.listeners = []; 
  }
}

const chatWS = new ChatWebSocket();
export default chatWS;
